import { Link } from 'react-router-dom';
import { KpiTrendChart } from './KpiTrendChart';
import type { KpiSnapshot } from './kpiRollup';
import { DashboardChartSkeleton, DashboardMailListSkeleton } from './DashboardSectionSkeleton';
import { ErrorState } from '@/components/StateMessage/StateMessage';
import styles from '../../pages/DashboardPage.module.css';

type Mailbox = {
  id: string;
  label: string;
  sent_today: number;
  daily_limit: number;
  status: string;
  last_error: string | null;
};

type Props = {
  rangeDays: 7 | 30 | 90;
  snapshots: KpiSnapshot[];
  kpiLoading: boolean;
  kpiError: Error | null;
  mailboxes: Mailbox[] | undefined;
  mailLoading: boolean;
  mailError: Error | null;
  mailsSent: number;
};

export function DashboardMailSection({
  rangeDays,
  snapshots,
  kpiLoading,
  kpiError,
  mailboxes,
  mailLoading,
  mailError,
  mailsSent,
}: Props) {
  const mailboxCount = mailboxes?.length ?? 0;
  return (
    <div className={styles.panel}>
      <div className={styles.panelHead}>
        <h2 className={styles.panelTitle}>Mail</h2>
        <Link to="/app/mailboxen">Mailboxen →</Link>
      </div>

      <h3 className={styles.sectionSubtitle}>Trend ({rangeDays} dagen)</h3>
      {kpiLoading ? (
        <DashboardChartSkeleton />
      ) : kpiError ? (
        <ErrorState message={kpiError.message} />
      ) : snapshots.length === 0 || mailsSent === 0 ? (
        <div className={styles.muted}>
          <p>
            {mailboxCount > 0
              ? `${mailboxCount} mailbox${mailboxCount === 1 ? '' : 'en'} gekoppeld. `
              : ''}
            Nog geen mail verzonden in deze periode.
          </p>
          <p className={styles.emptyActions}>
            <Link to="/app/campagnes">Campagnes bekijken</Link>
            {' · '}
            <Link to="/app/mailboxen">Mailboxen</Link>
          </p>
        </div>
      ) : (
        <KpiTrendChart snapshots={snapshots} />
      )}

      <h3 className={styles.sectionSubtitle}>Mailboxen vandaag</h3>
      {mailLoading ? (
        <DashboardMailListSkeleton />
      ) : mailError ? (
        <ErrorState message={mailError.message} />
      ) : !mailboxes?.length ? (
        <p className={styles.muted}>
          Geen mailboxen voor dit project.{' '}
          <Link to="/app/mailboxen">Mailbox instellen</Link>
        </p>
      ) : (
        <ul className={styles.mailList}>
          {mailboxes.map((mb) => {
            const pct =
              mb.daily_limit > 0
                ? Math.min(100, Math.round((mb.sent_today / mb.daily_limit) * 100))
                : 0;
            return (
              <li key={mb.id} className={styles.mailRow}>
                <div className={styles.mailRowHead}>
                  <span>{mb.label}</span>
                  <span>
                    {mb.sent_today}/{mb.daily_limit} · {mb.status}
                  </span>
                </div>
                <div className={styles.progressTrack}>
                  <progress
                    className={styles.progressNative}
                    max={100}
                    value={pct}
                    aria-label={`${mb.label} verzonden vandaag`}
                  />
                </div>
                {mb.last_error ? <p className={styles.mailError}>{mb.last_error}</p> : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
